import {
  Box,
  DatePicker,
  DatePickerProps,
  Flex,
  RadioGroup,
  RadioTile,
} from '@utilitywarehouse/hearth-react';
import dayjs from 'dayjs';
import { useEffect } from 'react';

import {
  type BroadbandAppointment,
  BroadbandAppointmentSlot,
} from '@generated/gql/graphql';
import { useSyncState } from '@hooks/useSyncState';
import { useAppointmentContext } from '@providers/AppointmentProvider/AppointmentContext';
import { DateFormat } from '@src/types/index';
import { useTrackSignupChangeBroadbandAppointmentComponentViewedOnce } from '@tracking/trackingEvents';

import { AppointmentStateKeys } from '../constants';
import { type SelectedAppointment } from '../types';
import { NextAvailableSlots } from './NextAvailableSlots';

interface ChooseAppointmentSlotProps extends Pick<DatePickerProps, 'onChange'> {
  availableSlots: BroadbandAppointment[];
  disabled?: boolean;
  onSelectAppointment: (appointment: SelectedAppointment) => void;
  selectedAppointment: SelectedAppointment;
  currentAppointment?: BroadbandAppointment;
}

export const ChooseAppointmentSlot = ({
  availableSlots,
  disabled = false,
  onSelectAppointment,
  selectedAppointment,
  currentAppointment,
}: ChooseAppointmentSlotProps) => {
  const trackViewedOnce =
    useTrackSignupChangeBroadbandAppointmentComponentViewedOnce();
  const { canUpdateState } = useAppointmentContext();
  const availableDates = availableSlots.map((slot) => new Date(slot.date));
  const { slot: selectedSlot = null, suggested = false } = selectedAppointment;

  const [isDatePickerOpen, setIsDatePickerOpen] = useSyncState(
    AppointmentStateKeys.BroadbandDatePickerOpen,
    false
  );
  const [viewedDate, setViewedDate] = useSyncState<string | null>(
    AppointmentStateKeys.BroadbandDatePickerViewedDate,
    null
  );

  const [selectedDateValue, setSelectedDateValue] = useSyncState<string | null>(
    AppointmentStateKeys.BroadbandDatePickerSelectedDate,
    null
  );

  const handleSelectDate = (date: Date | null) => {
    if (disabled) {
      return;
    }

    setSelectedDateValue(date ? dayjs(date).format(DateFormat.ISO8601) : null);

    onSelectAppointment({
      date: date ? dayjs(date).format(DateFormat.ISO8601) : null,
      slot: null,
      suggested: false,
    });
  };

  const handleSelectSlot = (slot: BroadbandAppointmentSlot) => {
    if (disabled) {
      return;
    }

    onSelectAppointment({ date: selectedDateValue, slot, suggested: false });
  };

  const handleOpenCloseDatePicker = (open: boolean) => {
    if (!canUpdateState) {
      return;
    }

    setIsDatePickerOpen(open);
  };

  useEffect(() => {
    trackViewedOnce({
      available_appointments_returned_count: availableSlots.length,
    });
  }, [availableSlots.length, trackViewedOnce]);

  return (
    <Flex direction="column" spacing="xl">
      <DatePicker
        required
        disabled={disabled}
        placeholderText=""
        helperText="DD/MM/YYYY"
        label="Choose a specific date"
        onChange={handleSelectDate}
        selected={
          suggested
            ? null
            : selectedDateValue
              ? new Date(selectedDateValue)
              : null
        }
        includeDates={availableDates}
        open={isDatePickerOpen}
        onCalendarOpen={() => handleOpenCloseDatePicker(true)}
        onCalendarClose={() => handleOpenCloseDatePicker(false)}
        openToDate={viewedDate ? new Date(viewedDate) : availableDates[0]}
        onMonthChange={(date) => setViewedDate(date.toISOString())}
        onYearChange={(date) => setViewedDate(date.toISOString())}
      />
      {!!selectedDateValue && !suggested ? (
        <Flex direction="column" gap="100">
          <Box asChild width="100%">
            <RadioGroup
              label="Select an appointment on:"
              helperText={dayjs(selectedDateValue).format(
                DateFormat.DisplayFull
              )}
              onValueChange={handleSelectSlot}
              value={selectedSlot}
              disabled={disabled}
            >
              <RadioTile
                label="Morning (8am - 1pm)"
                value={BroadbandAppointmentSlot.Am}
              />
              <RadioTile
                label="Afternoon (1pm - 6pm)"
                value={BroadbandAppointmentSlot.Pm}
              />
            </RadioGroup>
          </Box>
        </Flex>
      ) : (
        <NextAvailableSlots
          disabled={disabled}
          availableSlots={availableSlots}
          selectedAppointment={selectedAppointment}
          onSelectAppointment={(appointment) =>
            onSelectAppointment({ ...appointment, suggested: true })
          }
          currentAppointment={currentAppointment}
        />
      )}
    </Flex>
  );
};
